/**
 * Интерфейс для локализованного текста с обязательным английским переводом
 */
interface LocaleText {
  [key: string]: string;
  en: string;
}

/**
 * Интерфейс для объекта с локализованными полями
 */
interface LocalizedRecord {
  [key: string]: string | LocaleText | undefined;
}

/**
 * Тип входных данных для функции локализации
 * Поддерживает объекты AiRequest, простые объекты с локализацией
 * и вложенные объекты с полями name и collection
 */
type LocalizedInput =
  | {
      name?: LocaleText | LocalizedRecord | Record<string, string>;
      collection?: LocaleText | LocalizedRecord | Record<string, string>;
      [key: string]: unknown;
    }
  | LocaleText
  | null
  | undefined;

/**
 * Получает локализованный текст из объекта с учетом приоритета полей
 *
 * Порядок проверки:
 * 1. collection[locale]
 * 2. collection.en
 * 3. name[locale]
 * 4. name.en
 * 5. [locale]
 * 6. en
 *
 * @param source - Объект с локализованными текстами
 * @param locale - Код локали (например, 'ru', 'en')
 * @returns Локализованный текст или пустую строку
 *
 * @example
 * // Простой объект с локализацией
 * getLocalizedName({ en: 'Hello', ru: 'Привет' }, 'ru') // 'Привет'
 *
 * // Объект с вложенными полями
 * getLocalizedName({
 *   collection: { en: 'Collection', ru: 'Коллекция' },
 *   name: { en: 'Name', ru: 'Имя' }
 * }, 'ru') // 'Коллекция'
 */
export function getLocalizedName(
  source: LocalizedInput,
  locale: string,
): string {
  if (!source) {
    return "";
  }

  // Если получили строку - возвращаем эту строку без дальнейших проверок
  if (typeof source === "string") {
    return source;
  }

  /**
   * Извлекает локализованное значение из объекта
   */
  const getLocaleValue = (
    obj: LocaleText | LocalizedRecord | Record<string, string> | undefined,
  ): string => {
    if (!obj || typeof obj !== "object") {
      return "";
    }

    const localeValue = obj[locale];
    const enValue = obj.en;

    // Проверяем значение для текущей локали
    if (typeof localeValue === "string" && localeValue.trim() !== "") {
      return localeValue;
    }

    // Проверяем английский fallback
    if (typeof enValue === "string" && enValue.trim() !== "") {
      return enValue;
    }

    return "";
  };

  if (typeof source !== "object") {
    return "";
  }

  // Проверяем поля в порядке приоритета
  const checks = [
    () => source.collection && getLocaleValue(source.collection as LocaleText),
    () => source.name && getLocaleValue(source.name as LocaleText),
    () => getLocaleValue(source as LocaleText),
  ];

  for (const check of checks) {
    const value = check();
    if (value) {
      return value;
    }
  }

  return "";
}
